import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "./AuthContext";
import AuthModal from "./AuthModal";
import AdminGrantUnlimitedModal from "../components/AdminGrantUnlimitedModal";
import { useNavigate } from "react-router-dom";

// PFP circle that lives in the TopBar. Clicking it opens the auth modal when
// signed out, or a small profile dropdown when signed in.
//
// v11.4 — Dropdown is rendered via a React Portal on `document.body` at a
// z-index of 9999. The DAW TopBar has `overflow-x-auto overflow-y-hidden`
// (added in v11.3 to make it scroll on narrow windows), which was clipping
// the absolutely-positioned dropdown BEHIND the arrangement grid and
// making its buttons unclickable. Portal escapes the TopBar's overflow +
// stacking context entirely so the menu always paints on top of the grid.
export default function ProfileCircle({ signInPill = false }) {
  const { user, isAdmin, ready, logout } = useAuth();
  const [openAuth, setOpenAuth] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const [openAdminGrant, setOpenAdminGrant] = useState(false);
  const [anchor, setAnchor] = useState(null); // { top, right } in viewport px
  // v19.9 — Safety net for the "🎁 Grant Unlimited" + "🛡️ Grant Admin"
  // menu items. The strict `isAdmin` check reads the boolean off the
  // auth context, which is populated from the /api/auth/me response. If
  // the production build served a stale JS bundle (mid-deploy) OR the
  // user shape returned by the backend hasn't refreshed since the last
  // login, `isAdmin` can be false even though the DB row has
  // `is_admin=true`. As a belt-and-braces measure, ALSO show the admin
  // menu items when the user's email starts with "donnell" (case-
  // insensitive) — so the platform owner always has a path back into
  // his own admin UI without a re-login.
  const emailLc = (user?.email || "").toLowerCase();
  const showAdminItems = isAdmin || emailLc.startsWith("donnell");
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const nav = useNavigate();

  // Recompute the portal position whenever the menu opens OR the window
  // resizes / scrolls (a narrow window scrolling the TopBar horizontally
  // would otherwise leave the dropdown pinned in the wrong spot).
  const updateAnchor = React.useCallback(() => {
    const el = buttonRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setAnchor({
      top: r.bottom + 6,                        // just under the circle
      right: Math.max(8, window.innerWidth - r.right), // align right edges
    });
  }, []);

  useEffect(() => {
    if (!openMenu) return;
    updateAnchor();
    window.addEventListener("resize", updateAnchor);
    window.addEventListener("scroll", updateAnchor, true);
    return () => {
      window.removeEventListener("resize", updateAnchor);
      window.removeEventListener("scroll", updateAnchor, true);
    };
  }, [openMenu, updateAnchor]);

  // Outside-click closes the menu. Must check BOTH the trigger button and
  // the portal-rendered menu so clicks INSIDE the menu still work.
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpenMenu(false);
    };
    if (openMenu) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [openMenu]);

  const initial = (user?.username || user?.email || "?").slice(0, 1).toUpperCase();

  const menu = openMenu && user && anchor ? createPortal(
    <div
      ref={menuRef}
      className="w-64 rounded-xl p-3"
      style={{
        position: "fixed",
        top: anchor.top,
        right: anchor.right,
        zIndex: 9999,
        background: "linear-gradient(180deg,#0e0e0e,#070707)",
        border: "1px solid #2a2a2a",
        boxShadow: "0 14px 30px rgba(0,0,0,0.6)",
        pointerEvents: "auto",
      }}
      data-testid="profile-menu"
    >
      <div className="flex items-center gap-3 pb-3 border-b border-[#1d1d1d]">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm"
          style={{ background: "linear-gradient(135deg,#ec4899,#8b5cf6)" }}
        >{initial}</div>
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate" data-testid="profile-menu-name">{user.username || user.email}</div>
          {user.instagram_handle ? (
            <div className="text-[11px] text-pink-300/80 font-mono" data-testid="profile-menu-ig">@{user.instagram_handle}</div>
          ) : (
            <div className="text-[11px] text-white/30 font-mono">add an Instagram handle</div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 my-3">
        <Stat label="Beats made" value={user.beats_count ?? 0} testid="profile-menu-beats" />
        <Stat label="Plan" value={(user.plan || "free").toUpperCase()} />
      </div>

      <MenuItem label="My profile" onClick={() => { setOpenMenu(false); nav("/profile"); }} testid="profile-menu-profile" />
      <MenuItem label="Community" onClick={() => { setOpenMenu(false); nav("/community"); }} testid="profile-menu-community" />
      {showAdminItems && (
        <MenuItem label="🧪 AI Training Lab" onClick={() => { setOpenMenu(false); nav("/training"); }} testid="profile-menu-training" />
      )}
      {showAdminItems && (
        <MenuItem label="🎁 Grant Unlimited" onClick={() => { setOpenMenu(false); setOpenAdminGrant(true); }} testid="profile-menu-grant-unlimited" />
      )}
      {showAdminItems && (
        <MenuItem label="📈 Admin dashboard" onClick={() => { setOpenMenu(false); nav("/admin"); }} testid="profile-menu-admin" />
      )}
      <MenuItem label="Log out" onClick={() => { setOpenMenu(false); logout(); }} testid="profile-menu-logout" danger />
    </div>,
    document.body
  ) : null;

  return (
    <div ref={wrapRef} className="relative" data-testid="profile-circle-wrap">
      {signInPill && !user ? (
        <button
          onClick={() => setOpenAuth(true)}
          className="h-9 px-4 rounded-full text-xs font-black bg-white text-black hover:bg-emerald-300 transition-colors whitespace-nowrap shrink-0"
          data-testid="header-signin-btn"
          disabled={!ready}
        >
          Sign in
        </button>
      ) : (
      <button
        ref={buttonRef}
        onClick={() => (user ? setOpenMenu((v) => !v) : setOpenAuth(true))}
        className="relative w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition"
        style={{
          background: user
            ? "linear-gradient(135deg,#ec4899,#8b5cf6)"
            : "linear-gradient(135deg,#1a1a1a,#0a0a0a)",
          color: "white",
          border: user ? "2px solid #fff2" : "1.5px solid #2a2a2a",
          boxShadow: user ? "0 0 12px rgba(236,72,153,0.35)" : "inset 0 1px 0 rgba(255,255,255,0.05)",
        }}
        title={user ? `${user.username || user.email}` : "Sign in"}
        data-testid="profile-circle-btn"
        aria-label={user ? "Open profile menu" : "Sign in"}
        disabled={!ready}
      >
        {user ? initial : <span className="text-white/30 text-lg leading-none">+</span>}
      </button>
      )}

      {menu}

      <AuthModal open={openAuth} onClose={() => setOpenAuth(false)} />
      {showAdminItems && (
        <AdminGrantUnlimitedModal open={openAdminGrant} onClose={() => setOpenAdminGrant(false)} />
      )}
    </div>
  );
}

function MenuItem({ label, onClick, testid, danger }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left text-sm py-1.5 px-2 rounded hover:bg-white/5 transition"
      style={{ color: danger ? "#fca5a5" : "#fff" }}
      data-testid={testid}
    >
      {label}
    </button>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-md p-2 text-center" style={{ background: "#0a0a0a", border: "1px solid #1a1a1a" }}>
      <div className="text-[9px] uppercase tracking-wider text-white/40 font-mono">{label}</div>
      <div className="text-base font-extrabold" data-testid={testid}>{value}</div>
    </div>
  );
}
